import type { CrowdexTurnCheckpoint, CrowdexUIMessage } from '@crowdex/agent-contracts';
import type { CheckpointRevertTurn } from '@crowdex/workspace-protocol';

/** A file an assistant turn changed, with its path relative to the workspace. */
export type MessageChangedFile = CrowdexTurnCheckpoint['files'][number];

/** The workspace before and after an assistant turn, as the host's checkpoint store took it. */
export function messageCheckpoint(message: CrowdexUIMessage): CrowdexTurnCheckpoint | null {
  return message.role === 'assistant' ? (message.metadata?.checkpoint ?? null) : null;
}

/** Every file the turn changed: by its edit tools, its shell commands or anything they ran. */
export function messageChangedFiles(message: CrowdexUIMessage): MessageChangedFile[] {
  return messageCheckpoint(message)?.files ?? [];
}

/** The summary appears once the turn is over: its checkpoint is taken as it finishes. */
export function shouldShowMessageChanges(message: CrowdexUIMessage, isStreaming: boolean): boolean {
  return !isStreaming && messageChangedFiles(message).length > 0;
}

/**
 * A turn as a revert takes it: its trees, the files another conversation may have changed too
 * (they need the user's word) and those it wrote (never touched).
 */
export function checkpointRevertTurn(checkpoint: CrowdexTurnCheckpoint): CheckpointRevertTurn {
  return {
    before: checkpoint.before,
    after: checkpoint.after,
    shared: checkpoint.files.filter((file) => file.shared).map((file) => file.path),
    foreign: checkpoint.foreign ?? [],
  };
}

/**
 * The files a conversation's turns changed, from their checkpoints: those that are its own, and
 * those another conversation running in the same folder may have changed too. What another
 * conversation wrote is in neither.
 */
export function conversationChangedPaths(checkpoints: Iterable<CrowdexTurnCheckpoint>): {
  own: string[];
  shared: string[];
} {
  const own = new Set<string>();
  const shared = new Set<string>();
  for (const checkpoint of checkpoints) {
    for (const file of checkpoint.files) (file.shared ? shared : own).add(file.path);
  }
  for (const path of shared) own.delete(path);
  return { own: [...own].sort(), shared: [...shared].sort() };
}

/** {@link conversationChangedPaths} of a transcript's assistant turns. */
export function messagesChangedPaths(messages: readonly CrowdexUIMessage[]) {
  return conversationChangedPaths(
    messages.flatMap((message) => {
      const checkpoint = messageCheckpoint(message);
      return checkpoint === null ? [] : [checkpoint];
    }),
  );
}

/**
 * What editing the user message at `editedIndex` drops: the turns after it (oldest first) and the
 * files they changed. `null` when those turns changed nothing.
 */
export function rewindChanges(
  messages: readonly CrowdexUIMessage[],
  editedIndex: number,
): { turns: CheckpointRevertTurn[]; files: MessageChangedFile[] } | null {
  const dropped = messages.slice(editedIndex + 1).filter((message) => message.role === 'assistant');
  const turns: CheckpointRevertTurn[] = [];
  const files = new Map<string, MessageChangedFile>();
  for (const message of dropped) {
    const checkpoint = messageCheckpoint(message);
    if (checkpoint === null) continue;
    turns.push(checkpointRevertTurn(checkpoint));
    for (const file of checkpoint.files) {
      const seen = files.get(file.path);
      files.set(
        file.path,
        seen === undefined
          ? file
          : {
              ...file,
              additions: seen.additions + file.additions,
              deletions: seen.deletions + file.deletions,
              ...(seen.shared || file.shared ? { shared: true as const } : {}),
            },
      );
    }
  }
  return files.size === 0 ? null : { turns, files: [...files.values()] };
}
